<script>
  import { onDestroy, onMount } from "svelte";
  import {
    callQueue,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    menuConfig,
    directContext,
    currMenu,
    dataInfo,
    currentElementLockGroup,
  } from "../../editor/storage.js";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import { deepcopy } from "../../editor/utility.js";
  import { encodingMap, makeRGBAString } from "../../editor/component/mark.js";
  import LegendEditor from "../component/legendEditor.svelte";
  import { findLegend, unfindLegend } from "../../editor/component/legend.js";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let subMenu;
  let currLegends;
  let currLegendIndex;
  let legendCandidates;
  function setValues() {
    currLegends = [];
    legendCandidates = [];
    $currentSpec.fronto?.layer?.forEach((layer, li) => {
      let registeredLegends = [];
      layer.text?.forEach((textItem, ti) => {
        if (textItem.type === "legend") {
          let leg = deepcopy(textItem);
          leg.layerIndex = li;
          leg.layerName = layer.name;
          leg.textIndex = ti;
          let channel = leg.channel;
          leg.items?.forEach((item) => {
            if (item.custom !== undefined) {
              item.customList = [];
              Object.keys(item.custom).forEach((c) => {
                item.customList.push({
                  value: c,
                  text: item.custom[c],
                });
              });
            }
          });
          if (
            layer.mark?.[channel]?.field === leg.field ||
            (layer.mark?.[channel]?.field === undefined &&
              layer.mark?.[channel]?.aggregate === leg.aggregate)
          ) {
            registeredLegends.push(channel);
            currLegends.push(leg);
          }
        }
      });

      Object.keys(layer.mark)?.forEach((prop) => {
        if (
          Object.keys(encodingMap).includes(prop) &&
          (layer.mark[prop].field !== undefined ||
            layer.mark[prop].aggregate !== undefined) &&
          !registeredLegends.includes(prop)
        ) {
          legendCandidates.push({
            layerIndex: li,
            layerName: layer.name,
            channel: prop,
            field: layer.mark[prop].field,
            aggregate: layer.mark[prop].aggregate,
          });
        }
      });
    });
  }

  function customListToObj(list) {
    let obj = {};
    list.forEach((c) => {
      obj[c.value] = c.text;
    });
    return obj;
  }

  function updateLegend(event, role, legend) {
    let rule, newValue;
    if (role.endsWith("lock")) {
      updateLock(event, role.split(".")[0], legend);
      return;
    } else if (event === "delete" && role === "legend") {
      rule = {
        category: "manual",
        description: "Remove a legend for " + legend.channel,
        specifier: {
          role: "legend",
          id: legend.layerName,
          channel: legend.channel,
        },
        action: "remove",
      };
    } else {
      if (event === "new" && legend !== undefined) {
        newValue = true;
      } else if (role === "legend.values") {
        newValue = event.target.value.split(",");
        if ($dataInfo.stats[legend.field].rawType === "Number")
          newValue = newValue.map((d) => parseFloat(d.trim()));
      } else if (
        [
          "legend.orient",
          "legend.direction",
          "legend.title.text",
          "legend.title.textAlign",
          "legend.title.fontWeight",
          "legend.interactiveHighlight",
        ].includes(role)
      ) {
        newValue = event.target.value;
      } else if (
        [
          "legend.offset",
          "legend.gap",
          "legend.x",
          "legend.y",
          "legend.title.fontSize",
          "legend.title.opacity",
          "legend.title.marginTop",
          "legend.title.marginBottom",
          "legend.title.marginLeft",
          "legend.title.marginRight",
        ].includes(role)
      ) {
        newValue = parseFloat(event.target.value);
      } else if (role === "legend.items") {
        if (event === "add") {
          newValue = true;
        }
      } else if (role.match(/^legend\.items\.[0-9]+$/)) {
        if (event === "delete") {
          newValue = null;
        }
      } else if (
        role.match(
          /^legend\.items\.[0-9]+\.(width|color|fontSize|fontWeight|opacity|format|expression|custom)$/
        )
      ) {
        let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
        if (itemIndex !== undefined) {
          if (
            role.endsWith(".width") ||
            role.endsWith(".fontSize") ||
            role.endsWith(".opacity")
          ) {
            newValue = parseFloat(event.target.value);
          } else if (
            role.endsWith(".fontWeight") ||
            role.endsWith(".format") ||
            role.endsWith(".expression")
          ) {
            newValue = event.target.value;
          } else if (role.endsWith(".color")) {
            newValue = makeRGBAString(
              event.detail.r,
              event.detail.g,
              event.detail.b,
              event.detail.a
            );
          } else if (role.endsWith(".custom")) {
            newValue = customListToObj(
              event?.constructor.name === "Array"
                ? event
                : legend.items?.[itemIndex]?.customList || []
            );
            if (event.newCustomValue !== undefined) {
              newValue[event.newCustomValue] = event.newCustomText;
            }
          }
        }
      } else if (role === "legend.title") {
        newValue = event.target.checked || null;
      } else if (role === "legend.title.color") {
        newValue = makeRGBAString(
          event.detail.r,
          event.detail.g,
          event.detail.b,
          event.detail.a
        );
      }

      if (newValue !== undefined || newValue == null) {
        if (event === "new") {
          rule = {
            description: "Add a new legend for " + legend.channel,
            specifier: {
              role: "layer",
              index: legend.layerIndex,
              id: legend.layerName,
            },
            action: "add",
            option: { role: "legend", channel: legend.channel },
          };
          if (legend.field) rule.option.field = legend.field;
          else if (legend.aggregate) rule.option.aggregate = legend.aggregate;
        } else {
          rule = {
            description: "Edit legend for " + legend.channel + " ",
            specifier: {
              role: "legend",
              channel: legend.channel,
              layer: {},
            },
            action: "modify",
            option: {},
          };
          if (legend.field) rule.specifier.field = legend.field;
          else if (legend.aggregate)
            rule.specifier.aggregate = legend.aggregate;
          if (legend.layerName) rule.specifier.layer.id = legend.layerName;
          else if (legend.layerIndex !== undefined)
            rule.specifier.layer.index = legend.layerIndex;
          if (role === "legend.values") {
            rule.description += "values";
            rule.option = { values: newValue };
          } else if (role === "legend.orient") {
            rule.description += "orient";
            if (newValue !== "none")
              rule.option = { orient: newValue, dx: null, dy: null };
            else rule.option = { orient: newValue, dx: 0, dy: 0 }; //top-left as default
          } else if (role === "legend.direction") {
            rule.description += "direction";
            rule.option = { direction: newValue };
          } else if (role === "legend.offset") {
            rule.description += "offset";
            rule.option = { offset: newValue };
          } else if (role === "legend.x") {
            rule.description += "x";
            rule.option = { dx: newValue };
          } else if (role === "legend.y") {
            rule.description += "x";
            rule.option = { dy: newValue };
          } else if (role === "legend.gap") {
            rule.description += "gap";
            rule.option = { gap: newValue };
          } else if (role === "legend.items") {
            if (event === "add") {
              rule.description += "item (set)";
              rule.action = "add";
              rule.option = { items: {} };
            }
          } else if (role.match(/^legend\.items\.[0-9]+$/)) {
            let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
            if (event === "delete") {
              rule.description += "item (unset)";
              rule.action = "remove";
              rule.option = { items: { index: itemIndex } };
            }
          } else if (
            role.match(
              /^legend\.items\.[0-9]+\.(width|color|fontSize|fontWeight|opacity|format|expression|custom)$/
            )
          ) {
            let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
            rule.description += "item (" + itemIndex + ") ";
            if (itemIndex !== undefined) {
              if (role.endsWith("width")) {
                rule.description += "width";
                rule.option = { items: { index: itemIndex, width: newValue } };
              } else if (role.endsWith("fontSize")) {
                rule.description += "font size";
                rule.option = {
                  items: { index: itemIndex, fontSize: newValue },
                };
              } else if (role.endsWith("opacity")) {
                rule.description += "opacity";
                rule.option = {
                  items: { index: itemIndex, opacity: newValue },
                };
              } else if (role.endsWith("fontWeight")) {
                rule.description += "font weight";
                rule.option = {
                  items: { index: itemIndex, fontWeight: newValue },
                };
              } else if (role.endsWith("color")) {
                rule.description += "font color";
                rule.option = { items: { index: itemIndex, color: newValue } };
              } else if (role.endsWith("format")) {
                rule.description += "format";
                rule.option = {
                  items: { index: itemIndex, format: newValue },
                };
              } else if (role.endsWith("expression")) {
                rule.description += "expression";
                rule.option = {
                  items: { index: itemIndex, expression: newValue },
                };
              } else if (role.endsWith("custom")) {
                rule.description += "custom";
                rule.option = {
                  items: { index: itemIndex, custom: newValue },
                };
              }
            }
          } else if (role.startsWith("legend.title")) {
            if (role === "legend.title") {
              rule.description += `title (${newValue ? "set" : "unset"})`;
              rule.action = "modify";
              rule.option = { title: newValue };
            } else {
              let subRole = role.split(".")[2];
              rule.description += "title " + subRole;
              rule.action = "modify";
              rule.option = { title: {} };
              rule.option.title[subRole] = newValue;
            }
          } else if (role === "legend.interactiveHighlight") {
            rule.description += "interactive highlight";
            rule.option = { interactiveHighlight: newValue };
          }
        }
      }
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  function setSubMenu(s, i) {
    subMenu = s;
    currLegendIndex = i;
  }
  function cancelSubMenu() {
    subMenu = undefined;
    currLegendIndex = undefined;
  }
  // from external
  menuConfig.subscribe((config) => {
    if (
      config?.subMenu &&
      config?.layerIndex !== undefined &&
      config?.channel
    ) {
      setValues();
      let li = currLegends
        ?.map((d, i) => {
          return d.layerIndex == config.layerIndex &&
            d.channel === config.channel
            ? i
            : undefined;
        })
        .filter((d) => d !== undefined)[0];
      setSubMenu(config.subMenu, li);
    }
  });
  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    cancelSubMenu();
  });
  function updateLock(event, role, legend) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role && legend) {
        let rule = {
          category: "lock",
          description:
            (newValue ? "Lock" : "Unlock") + " legend for " + legend.channel,
          specifier: {
            role: "legend",
            channel: legend.channel,
            layer: {},
          },
          action: "lock",
          option: {},
        };
        if (legend.field) rule.specifier.field = legend.field;
        else if (legend.aggregate) rule.specifier.aggregate = legend.aggregate;
        if (legend.layerName) rule.specifier.layer.id = legend.layerName;
        else if (legend.layerIndex !== undefined)
          rule.specifier.layer.index = legend.layerIndex;
        if (role === "legend") {
          rule.option.legend = newValue;
        } else if (role === "position") {
          rule.option.position = newValue;
        }

        if (rule) {
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["legend"]?.title || "Legend"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !subMenu}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Select legend to edit or create a new one.</p>
      </div>
    </div>
    {#if legendCandidates.length > 0}
      <div class="pane-divider" />
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-form-row">
          <button
            class="menu-second-level"
            on:click={() => {
              setSubMenu("new");
            }}><i class="bi bi-plus-circle me-1" /> Add a new legend</button
          >
        </div>
      </div>
    {/if}
    <div class="pane-divider" />
    <div class="dupo-menu-pane mb-1">
      {#if currLegends.length > 0}
        <div class="dupo-menu-pane-row">
          <h3 class="h-sub">Existing legends</h3>
        </div>
        {#each currLegends as legend, li}
          <div class="dupo-menu-pane-form-row">
            <div
              class="menu-second-level menu-with-buttons"
              on:mouseover={() => {
                findLegend($currentSpec.uid, legend);
              }}
              on:focus={() => {
                findLegend($currentSpec.uid, legend);
              }}
              on:mouseout={() => {
                unfindLegend();
              }}
              on:blur={() => {
                unfindLegend();
              }}
            >
              <div class="text-truncate" style="user-select:none;">
                <img
                  src={`../image/encoding/${encodingMap[legend.channel]?.icon}`}
                  class="enc-icon"
                  alt={encodingMap[legend.channel]?.name + " icon"}
                />{legend.layerName} / {legend.channel}
                <i class="bi bi-caret-right-fill my-1">
                  {legend.field || legend.aggregate}
                </i>
              </div>
              <div class="dupo-pane-list-button">
                <button
                  type="button"
                  on:click={() => {
                    setSubMenu("edit", li);
                  }}
                  ><i
                    class="bi bi-pencil-fill"
                    alt="{`Edit the legend for ${legend.channel} channel`}}"
                  /></button
                >
              </div>
            </div>
          </div>
        {/each}
      {/if}
    </div>
  {:else if subMenu == "new"}
    <LegendEditor
      status="new"
      {inputFocusAction}
      {inputBlurAction}
      {legendCandidates}
      {updateLegend}
    />
    <div class="pane-divider" />
    <div class="dupo-menu-pane pb-2">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else {
              unfindLegend();
              cancelSubMenu();
            }
          }}
        >
          <i class="bi bi-arrow-left me-1" /> Go back</button
        >
      </div>
    </div>
  {:else if subMenu == "edit" && currLegendIndex !== undefined}
    <LegendEditor
      status="edit"
      legend={currLegends[currLegendIndex]}
      {inputFocusAction}
      {inputBlurAction}
      {updateLegend}
      afterDelete={() => {
        if ($directContext) currMenu.set("selection");
        else {
          unfindLegend();
          cancelSubMenu();
        }
      }}
    />
    <div class="pane-divider" />
    <div class="dupo-menu-pane pb-2">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else {
              unfindLegend();
              cancelSubMenu();
            }
          }}
        >
          <i class="bi bi-arrow-left me-1" /> Go back</button
        >
      </div>
    </div>
  {/if}
</section>
